import { Controller } from "@hotwired/stimulus"

// Reusable radar / spider chart. Draws a crisp SVG: N evenly spaced axes,
// gridline rings, one polygon per series (brand_color fill at low alpha +
// stroke). Up to ~6 series, reads well from 3 to 6 axes. Hover / touch on a
// legend entry isolates that series; hover on a vertex shows its value.
//
// Theme colors use color-mix on real daisyUI v5 vars (--color-base-content)
// so gridlines and labels adapt to light / dark. Series strokes use the
// brand_color passed in the data.

interface RadarAxis {
  slug: string
  label: string
  icon?: string | null
}

interface RadarSeries {
  model_id: string
  slug: string
  name: string
  brand_color: string | null
  values: number[]
}

interface RadarData {
  axes: RadarAxis[]
  series: RadarSeries[]
}

const SVG_NS = "http://www.w3.org/2000/svg"

// Distinct categorical palette for the series lines. We deliberately do NOT use
// each model's creator brand_color: several models share a creator (all the
// FLUX lines, all the Gemini lines), so brand colors collapse to one hue and
// the overlapping spider becomes unreadable. Assigning by series index instead
// guarantees every line is a different, well-separated colour. Mid-lightness +
// moderate chroma in oklch so each reads on both the light and dark themes.
const SERIES_PALETTE = [
  "oklch(0.62 0.16 250)", // blue
  "oklch(0.70 0.17 55)",  // orange
  "oklch(0.62 0.16 150)", // green
  "oklch(0.60 0.21 350)", // magenta
  "oklch(0.66 0.12 210)", // teal
  "oklch(0.56 0.19 295)", // purple
  "oklch(0.74 0.15 85)",  // amber
  "oklch(0.60 0.21 25)",  // red
]

// Fraction of the box half-extent the polygon radius occupies; the rest is
// label gutter. Axis labels (some long, e.g. "Product, Branding & Commercial")
// sit outside the rim, so the viewBox also gets horizontal padding below.
const LABEL_MARGIN = 0.72
// Horizontal padding added to each side of the square box so side labels live
// inside the chart's own coordinate space and never clip against the page.
const HPAD_RATIO = 0.32
const RING_COUNT = 4

export default class extends Controller {
  static targets = ["chart", "legend"]
  static values = {
    data: Object,
    box: { type: Number, default: 360 },
  }

  declare chartTarget: HTMLElement
  declare hasChartTarget: boolean
  declare legendTarget: HTMLElement
  declare hasLegendTarget: boolean
  declare dataValue: RadarData
  declare boxValue: number

  private svg!: SVGSVGElement
  private tooltip!: HTMLDivElement
  private size = 360
  private vbW = 360
  private vbH = 360
  private cx = 180
  private cy = 180
  private radius = 120
  private colors!: { grid: string; spoke: string; label: string; ring: string; muted: string }
  private seriesPolys = new Map<string, SVGPolygonElement>()
  private activeSlug: string | null = null

  connect() {
    const data = this.dataValue
    if (!data || !Array.isArray(data.axes) || data.axes.length < 3) return
    if (!Array.isArray(data.series) || data.series.length === 0) return

    this.size = this.boxValue
    const hpad = Math.round(this.size * HPAD_RATIO)
    this.vbW = this.size + hpad * 2
    this.vbH = this.size
    this.cx = this.vbW / 2
    this.cy = this.vbH / 2
    this.radius = (this.size / 2) * LABEL_MARGIN

    const bc = "var(--color-base-content)"
    this.colors = {
      ring:  `color-mix(in oklch, ${bc} 9%, transparent)`,
      grid:  `color-mix(in oklch, ${bc} 14%, transparent)`,
      spoke: `color-mix(in oklch, ${bc} 12%, transparent)`,
      label: `color-mix(in oklch, ${bc} 68%, transparent)`,
      muted: `color-mix(in oklch, ${bc} 45%, transparent)`,
    }

    this.render()
  }

  private render() {
    const { axes, series } = this.dataValue
    const n = axes.length

    const container = this.hasChartTarget ? this.chartTarget : (this.element as HTMLElement)
    container.innerHTML = ""

    this.svg = document.createElementNS(SVG_NS, "svg")
    this.svg.setAttribute("viewBox", `0 0 ${this.vbW} ${this.vbH}`)
    this.svg.setAttribute("role", "img")
    this.svg.setAttribute("aria-label", "Radar chart comparing models across categories")

    // Wrap canvas so the tooltip can position absolutely over the SVG.
    const wrap = document.createElement("div")
    wrap.style.position = "relative"

    this.tooltip = document.createElement("div")
    this.tooltip.className =
      "absolute pointer-events-none opacity-0 transition-opacity duration-150 bg-base-100 border border-base-200 rounded-lg shadow-lg px-2.5 py-1.5 text-xs z-50 whitespace-nowrap text-base-content"
    this.tooltip.style.transform = "translate(-50%, -100%)"

    // Layers: rings -> spokes -> labels -> polygons -> vertices.
    this.drawRings(n)
    this.drawSpokes(n)
    this.drawAxisLabels(axes)

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const drawn = series.slice(0, 6)
    drawn.forEach((s, i) => this.drawSeries(s, n, i, reduceMotion, drawn.length))

    wrap.appendChild(this.svg)
    wrap.appendChild(this.tooltip)
    container.appendChild(wrap)

    this.buildLegend(series.slice(0, 6))
  }

  // Geometry: axis i points from straight up (-90deg) clockwise.
  private angleFor(i: number, n: number): number {
    return -Math.PI / 2 + (i * 2 * Math.PI) / n
  }

  private pointAt(i: number, n: number, valueFraction: number): { x: number; y: number } {
    const a = this.angleFor(i, n)
    const r = this.radius * valueFraction
    return { x: this.cx + r * Math.cos(a), y: this.cy + r * Math.sin(a) }
  }

  private drawRings(n: number) {
    for (let ring = 1; ring <= RING_COUNT; ring++) {
      const frac = ring / RING_COUNT
      const pts: string[] = []
      for (let i = 0; i < n; i++) {
        const p = this.pointAt(i, n, frac)
        pts.push(`${p.x.toFixed(2)},${p.y.toFixed(2)}`)
      }
      const poly = document.createElementNS(SVG_NS, "polygon")
      poly.setAttribute("points", pts.join(" "))
      poly.setAttribute("fill", "none")
      poly.style.stroke = ring === RING_COUNT ? this.colors.grid : this.colors.ring
      poly.style.strokeWidth = "1"
      this.svg.appendChild(poly)
    }
  }

  private drawSpokes(n: number) {
    for (let i = 0; i < n; i++) {
      const outer = this.pointAt(i, n, 1)
      const line = document.createElementNS(SVG_NS, "line")
      line.setAttribute("x1", this.cx.toFixed(2))
      line.setAttribute("y1", this.cy.toFixed(2))
      line.setAttribute("x2", outer.x.toFixed(2))
      line.setAttribute("y2", outer.y.toFixed(2))
      line.style.stroke = this.colors.spoke
      line.style.strokeWidth = "1"
      this.svg.appendChild(line)
    }
  }

  private drawAxisLabels(axes: RadarAxis[]) {
    const n = axes.length
    for (let i = 0; i < n; i++) {
      const a = this.angleFor(i, n)
      // Push labels just outside the outermost ring.
      const lr = this.radius + this.size * 0.05
      const x = this.cx + lr * Math.cos(a)
      const y = this.cy + lr * Math.sin(a)

      const cos = Math.cos(a)
      let anchor: string
      if (Math.abs(cos) < 0.3) anchor = "middle"
      else if (cos > 0) anchor = "start"
      else anchor = "end"

      // Nudge vertical baseline so top / bottom labels clear the ring.
      const sin = Math.sin(a)
      const dy = Math.abs(sin) < 0.3 ? 0.32 : sin > 0 ? 0.86 : -0.2

      const text = document.createElementNS(SVG_NS, "text")
      text.setAttribute("x", x.toFixed(2))
      text.setAttribute("y", y.toFixed(2))
      text.setAttribute("text-anchor", anchor)
      text.setAttribute("dy", `${dy}em`)
      text.setAttribute("font-size", this.size >= 400 ? "14" : "13")
      text.setAttribute("font-family", "system-ui, sans-serif")
      text.setAttribute("font-weight", "600")
      text.style.fill = this.colors.label
      text.textContent = axes[i].label
      this.svg.appendChild(text)
    }
  }

  private clampFraction(v: number): number {
    if (!Number.isFinite(v)) return 0
    const f = v / 100
    return Math.max(0, Math.min(1, f))
  }

  private seriesColor(index: number): string {
    return SERIES_PALETTE[index % SERIES_PALETTE.length]
  }

  private drawSeries(s: RadarSeries, n: number, index: number, reduceMotion: boolean, seriesCount: number) {
    // Distinct per-index colour, NOT the creator brand colour (see SERIES_PALETTE).
    const stroke = this.seriesColor(index)
    // A lone "signature" series gets a whisper of fill for body; the moment we
    // overlay more than one (battle / top-models compare) we go stroke-only so
    // the shapes don't occlude each other and the area doesn't over-dramatize
    // differences the axes already encode.
    const fill = seriesCount > 1
      ? "none"
      : `color-mix(in oklch, ${stroke} 12%, transparent)`

    const pts: string[] = []
    for (let i = 0; i < n; i++) {
      const frac = this.clampFraction(s.values?.[i] ?? 0)
      const p = this.pointAt(i, n, frac)
      pts.push(`${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    }

    const poly = document.createElementNS(SVG_NS, "polygon")
    poly.setAttribute("points", pts.join(" "))
    poly.style.fill = fill
    poly.style.stroke = stroke
    poly.style.strokeWidth = "2"
    poly.setAttribute("stroke-linejoin", "round")
    poly.dataset.slug = s.slug
    poly.style.transition = "opacity 0.15s ease"
    poly.style.pointerEvents = "none"
    this.svg.appendChild(poly)
    this.seriesPolys.set(s.slug, poly)

    // Invisible wide-stroke twin that makes the whole line hoverable: hovering
    // a line isolates that series (dims the others) and lights up its legend
    // entry, the mirror of hovering the legend. Transparent paint + a fat
    // stroke + pointer-events:stroke give a forgiving hit target on a 2px line.
    const hit = document.createElementNS(SVG_NS, "polygon")
    hit.setAttribute("points", pts.join(" "))
    hit.style.fill = "none"
    hit.style.stroke = "transparent"
    hit.style.strokeWidth = "16"
    hit.style.strokeLinejoin = "round"
    hit.style.pointerEvents = "stroke"
    hit.style.cursor = "pointer"
    hit.dataset.slug = s.slug
    hit.addEventListener("mouseenter", () => this.isolate(s.slug))
    hit.addEventListener("mouseleave", () => this.isolate(null))
    this.svg.appendChild(hit)

    // Vertices: small dots that surface the per-point value on hover / touch.
    for (let i = 0; i < n; i++) {
      const frac = this.clampFraction(s.values?.[i] ?? 0)
      const p = this.pointAt(i, n, frac)
      const dot = document.createElementNS(SVG_NS, "circle")
      dot.setAttribute("cx", p.x.toFixed(2))
      dot.setAttribute("cy", p.y.toFixed(2))
      dot.setAttribute("r", "3")
      dot.style.fill = stroke
      dot.style.cursor = "pointer"
      dot.dataset.slug = s.slug

      const rawValue = Math.round(s.values?.[i] ?? 0)
      const axisLabel = this.dataValue.axes[i].label
      dot.addEventListener("mouseenter", () => this.showTooltip(p.x, p.y, s.name, axisLabel, rawValue))
      dot.addEventListener("mouseleave", () => this.hideTooltip())
      dot.addEventListener(
        "touchstart",
        (e) => { e.preventDefault(); this.showTooltip(p.x, p.y, s.name, axisLabel, rawValue) },
        { passive: false }
      )
      this.svg.appendChild(dot)
    }

    // Animate the polygon draw with an ease-out scale from the center.
    // Transforms only (no layout props), and skipped under reduced-motion.
    if (!reduceMotion) {
      poly.style.transformBox = "fill-box"
      poly.style.transformOrigin = "center"
      poly.animate(
        [
          { transform: "scale(0.2)", opacity: 0 },
          { transform: "scale(1)", opacity: 1 },
        ],
        { duration: 520, delay: index * 90, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "backwards" }
      )
    }
  }

  private buildLegend(series: RadarSeries[]) {
    if (!this.hasLegendTarget) return
    this.legendTarget.innerHTML = ""

    series.forEach((s, index) => {
      // Link each legend entry to the model's arena page when a slug is present;
      // fall back to a non-navigating span otherwise. A real anchor keeps it
      // keyboard-focusable and lets the browser show / open the URL natively.
      const hasSlug = typeof s.slug === "string" && s.slug.length > 0
      const item = document.createElement(hasSlug ? "a" : "span") as HTMLElement
      item.className = "bm-radar-legend-item"
      item.dataset.slug = s.slug
      if (hasSlug) {
        ;(item as HTMLAnchorElement).href = `/arena/models/${encodeURIComponent(s.slug)}`
      }

      const swatch = document.createElement("span")
      swatch.className = "bm-radar-legend-swatch"
      // Match the line's distinct palette colour, not the creator brand colour.
      swatch.style.background = this.seriesColor(index)

      const label = document.createElement("span")
      label.textContent = s.name

      item.appendChild(swatch)
      item.appendChild(label)

      item.addEventListener("mouseenter", () => this.isolate(s.slug))
      item.addEventListener("mouseleave", () => this.isolate(null))
      item.addEventListener("touchstart", () => this.isolate(this.activeSlug === s.slug ? null : s.slug), { passive: true })

      this.legendTarget.appendChild(item)
    })
  }

  // Hovering a legend entry fades the other polygons so the hovered series reads
  // clearly. Passing null restores all.
  private isolate(slug: string | null) {
    this.activeSlug = slug
    for (const [s, poly] of this.seriesPolys) {
      poly.style.opacity = slug && s !== slug ? "0.12" : "1"
    }
    if (this.hasLegendTarget) {
      for (const item of Array.from(this.legendTarget.children) as HTMLElement[]) {
        const dim = !!slug && item.dataset.slug !== slug
        item.dataset.dimmed = String(dim)
      }
    }
  }

  private showTooltip(svgX: number, svgY: number, name: string, axisLabel: string, value: number) {
    const rect = this.svg.getBoundingClientRect()
    const scale = rect.width / this.vbW
    this.tooltip.style.left = `${svgX * scale}px`
    this.tooltip.style.top = `${svgY * scale - 8}px`
    this.tooltip.innerHTML = `
      <div class="font-semibold mb-0.5">${this.escapeHtml(name)}</div>
      <div>${this.escapeHtml(axisLabel)}: <strong>${value}</strong></div>
    `
    this.tooltip.classList.remove("opacity-0")
    this.tooltip.classList.add("opacity-100")
  }

  private hideTooltip() {
    this.tooltip.classList.remove("opacity-100")
    this.tooltip.classList.add("opacity-0")
  }

  private escapeHtml(s: string): string {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }
}
